/**
 * `/studio/t/:gtwinId` — gTwin Structure Studio route (ADR-120 / WP-B).
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { tokens } from '@/design';
import type { GTwinRecord } from '@/sdk-stub/gtwin-sdk/types';
import { NotFoundError } from '@/sdk-stub/gtwin-sdk';
import { gtwinClient } from './gtwinClientSingleton';
import { RouteScaffold } from './RouteScaffold';
import { StructureStudioShell } from './StructureStudioShell';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; record: GTwinRecord }
  | { status: 'not-found' }
  | { status: 'error'; message: string };

export function StructureStudioRoute() {
  const { gtwinId = '' } = useParams<{ gtwinId: string }>();
  const [loadState, setLoadState] = useState<LoadState>({ status: 'loading' });

  const reload = useCallback(async () => {
    if (!gtwinId) return;
    const record = await gtwinClient.get(gtwinId);
    setLoadState({ status: 'loaded', record });
  }, [gtwinId]);

  useEffect(() => {
    if (!gtwinId) return;
    let cancelled = false;
    setLoadState({ status: 'loading' });
    void (async () => {
      try {
        const record = await gtwinClient.get(gtwinId);
        if (!cancelled) setLoadState({ status: 'loaded', record });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotFoundError) {
          setLoadState({ status: 'not-found' });
        } else {
          setLoadState({
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gtwinId]);

  if (loadState.status === 'loading') {
    return (
      <div
        role="status"
        data-testid="structure-studio-loading"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
        }}
      >
        Loading structure…
      </div>
    );
  }

  if (loadState.status === 'not-found') {
    return (
      <RouteScaffold
        gvwId="GVW-STUDIO-404"
        route={`/studio/t/${gtwinId}`}
        title="Project not found"
        body={`The project "${gtwinId}" could not be found.`}
        wpId="WP-B-STUDIO"
        data-testid="structure-studio-not-found"
      />
    );
  }

  if (loadState.status === 'error') {
    return (
      <RouteScaffold
        gvwId="GVW-STUDIO-ERR"
        route={`/studio/t/${gtwinId}`}
        title="Failed to load project"
        body={loadState.message}
        wpId="WP-B-STUDIO"
        data-testid="structure-studio-error"
      />
    );
  }

  return (
    <div data-testid="structure-studio-route" data-gtwin-id={gtwinId}>
      <StructureStudioShell
        gtwinId={gtwinId}
        initialRecord={loadState.record}
        onRecordChange={() => void reload()}
      />
    </div>
  );
}
